import { z } from 'zod';

/**
 * Danh mục vị trí kho (YC-07 phần 1, 30/10/2026). Quy tắc chốt theo chuẩn ERP, chờ khách xác nhận — `docs/PROJECT_DECISIONS.md`.
 *
 * Mã vị trí theo cách Roary đánh số: kệ + ô, mỗi tầng hai ô — tầng 1 = A/B, tầng 2 = C/D, tầng 3 = E/F ("36C" = kệ 36, tầng 2,
 * ô trái). Mã là giá trị ghi vào cột `location` của chứng từ, nên chuẩn hoá MỘT chỗ: chữ hoa, bỏ mọi khoảng trắng.
 */
export const BIN_SLOTS_PER_LEVEL = 2;
export const BIN_MAX_LEVELS = 6;
export const BIN_MAX_PER_GENERATE = 2000;

export function normalizeBinCode(raw: string): string {
 return raw.normalize('NFKC').replace(/\s+/g, '').toUpperCase();
}

/** Chữ của các ô ở một tầng: tầng 1 → A, B; tầng 2 → C, D; tầng 3 → E, F. */
export function slotLetters(level: number): string[] {
 return Array.from({ length: BIN_SLOTS_PER_LEVEL }, (_, i) => String.fromCharCode(65 + (level - 1) * BIN_SLOTS_PER_LEVEL + i));
}

export interface GeneratedBin { code: string; rack: number; level: number; slot: string; sort: number }

/** Mọi vị trí của dải kệ: kệ fromRack…toRack, tầng 1…levels. Thứ tự: theo kệ, rồi tầng, rồi ô. */
export function generateBins(fromRack: number, toRack: number, levels: number): GeneratedBin[] {
 const out: GeneratedBin[] = [];
 for (let rack = fromRack; rack <= toRack; rack++)
  for (let level = 1; level <= levels; level++)
   for (const slot of slotLetters(level)) out.push({ code: `${rack}${slot}`, rack, level, slot, sort: rack * 100 + (slot.charCodeAt(0) - 64) });
 return out;
}

const code = z.string().trim().min(1).max(40).transform(normalizeBinCode).refine(v => /^[A-Z0-9][A-Z0-9._/-]*$/.test(v), { message: 'bin_code_invalid' });
const zone = z.string().trim().max(60);

export const binGenerateSchema = z.object({
 warehouseId: z.string().min(1).max(100),
 fromRack: z.number().int().min(1).max(9999),
 toRack: z.number().int().min(1).max(9999),
 levels: z.number().int().min(1).max(BIN_MAX_LEVELS),
 zone: zone.default(''),
}).refine(v => v.toRack >= v.fromRack, { message: 'bin_range_invalid', path: ['toRack'] })
 .refine(v => (v.toRack - v.fromRack + 1) * v.levels * BIN_SLOTS_PER_LEVEL <= BIN_MAX_PER_GENERATE, { message: 'bin_range_too_large', path: ['toRack'] });
export type BinGenerateInput = z.infer<typeof binGenerateSchema>;

/** Một vị trí khai tay (khu để hàng chờ xuất, kệ đánh số khác quy tắc…). */
export const binCreateSchema = z.object({ warehouseId: z.string().min(1).max(100), code, zone: zone.default('') });
export type BinCreateInput = z.infer<typeof binCreateSchema>;

export const binUpdateSchema = z.object({ zone: zone.optional(), active: z.boolean().optional() });
export type BinUpdateInput = z.infer<typeof binUpdateSchema>;

/** Gán vùng cho cả dải kệ (vd kệ 1–8 = "五爪"). */
export const binZoneRangeSchema = z.object({
 warehouseId: z.string().min(1).max(100),
 fromRack: z.number().int().min(1).max(9999),
 toRack: z.number().int().min(1).max(9999),
 zone,
}).refine(v => v.toRack >= v.fromRack, { message: 'bin_range_invalid', path: ['toRack'] });
export type BinZoneRangeInput = z.infer<typeof binZoneRangeSchema>;

export const binListQuerySchema = z.object({ warehouseId: z.string().min(1).max(100) });

export const stockByLocationQuerySchema = z.object({
 warehouseId: z.string().max(100).default(''),
 zone: z.string().trim().max(60).default(''),
 q: z.string().trim().max(100).default(''),
 /** Chỉ hàng đang nằm ở vị trí NGOÀI danh mục (gõ tự do trước khi khai danh mục) — để chuyển về vị trí chuẩn. */
 outside: z.enum(['true', 'false']).default('false'),
 page: z.coerce.number().int().min(1).default(1),
});

export interface WarehouseBinDto {
 id: string; warehouseId: string; code: string; rack: number | null; level: number | null; slot: string | null;
 zone: string; active: boolean;
 /** Số dòng lô đang có hàng ở vị trí này. Vị trí còn hàng thì không xoá được, chỉ ngừng dùng. */
 stockLines: number;
}

export interface BinWarehouseDto { id: string; code: string; name: string; bins: number; activeBins: number }

export interface BinGenerateResult { created: number; skipped: number; codes: string[] }

export interface StockByLocationRow {
 warehouseId: string; warehouseName: string; location: string;
 /** Vị trí có trong danh mục của kho (kho chưa khai danh mục → false với mọi dòng). */
 inCatalog: boolean;
 /** Kho ĐÃ khai danh mục mà vị trí không có trong đó — hàng cần chuyển về vị trí chuẩn. Kho chưa khai → luôn false. */
 outsideCatalog: boolean; zone: string;
 productId: string; productCode: string; subRef: string; description: string;
 lotNumber: string; unit: string;
 quantity: string; availableQuantity: string;
}
